// ─── CMS Mock Data — Home Page Content ───

export interface CMSHeroSection {
  badge: string;
  badge_en?: string;
  heading1: string;
  heading1_en?: string;
  typewriterWords: string[];
  typewriterWords_en?: string[];
  description: string;
  description_en?: string;
  subDescription: string;
  subDescription_en?: string;
  btnBook: string;
  btnBook_en?: string;
  btnServices: string;
  btnServices_en?: string;
  pillars: string[];
  pillars_en?: string[];
  stats: { value: string; label: string; label_en?: string }[];
  backgroundImage: string;
}

export interface CMSWhyUsItem {
  icon: string;
  title: string;
  title_en?: string;
  description: string;
  description_en?: string;
}

export interface CMSWhyUsSection {
  badge: string;
  badge_en?: string;
  heading: string;
  heading_en?: string;
  subHeading: string;
  subHeading_en?: string;
  description: string;
  description_en?: string;
  subDescription: string;
  subDescription_en?: string;
  mainImage: string;
  items: CMSWhyUsItem[];
  items_en?: CMSWhyUsItem[];
}

export interface CMSCTASection {
  badge: string;
  badge_en?: string;
  heading: string;
  heading_en?: string;
  description: string;
  description_en?: string;
  subDescription: string;
  subDescription_en?: string;
  btnBook: string;
  btnBook_en?: string;
  btnContact: string;
  btnContact_en?: string;
}

export interface CMSTrustBarItem {
  icon: string;
  value: string;
  label: string;
  label_en?: string;
}

export interface CMSTrustBarSection {
  items: CMSTrustBarItem[];
}

export interface CMSTestimonialItem {
  name: string;
  name_en?: string;
  role: string;
  role_en?: string;
  text: string;
  text_en?: string;
  rating: number;
  avatar: string;
}

export interface CMSTestimonialsSection {
  badge: string;
  badge_en?: string;
  heading: string;
  heading_en?: string;
  description: string;
  description_en?: string;
  items: CMSTestimonialItem[];
  config?: {
    sectionBadge?: string;
    sectionBadge_en?: string;
    sectionTitle?: string;
    sectionTitle_en?: string;
    sectionSubtitle?: string;
    sectionSubtitle_en?: string;
    displayStyle?: string;
    showRating?: boolean;
    showImage?: boolean;
  };
}

export interface CMSContent {
  hero: CMSHeroSection;
  whyUs: CMSWhyUsSection;
  cta: CMSCTASection;
  trustBar: CMSTrustBarSection;
  testimonials: CMSTestimonialsSection;
  lastUpdated: string;
}

export const cmsContent: CMSContent = {
  hero: {
    badge: "مركز الطب الدقيق والعافية المتكاملة",
    badge_en: "Precision Medicine & Integrated Wellness Center",
    heading1: "صحتك تستحق",
    heading1_en: "Your Health Deserves",
    typewriterWords: ["فهماً أعمق", "رعاية أدق", "علماً حقيقياً", "مساراً مخصصاً"],
    typewriterWords_en: ["Deeper Understanding", "Precise Care", "True Science", "Customized Path"],
    description: "نقدم طبًا يقرأ ما وراء الأعراض — يربط بين الجينات، الأيض، والعمر البيولوجي لبناء خطة صحية حقيقية لك.",
    description_en: "We practice medicine that looks beyond symptoms — connecting genetics, metabolism, and biological age to build a true health roadmap for you.",
    subDescription: "عيادة متخصصة في الطب الدقيق، الطب الوظيفي، وإدارة الأمراض المزمنة بأسلوب علمي متكامل.",
    subDescription_en: "Specialized clinic in precision medicine, functional medicine, and chronic disease management through a comprehensive scientific approach.",
    btnBook: "احجز موعدك",
    btnBook_en: "Book Your Appointment",
    btnServices: "استكشف خدماتنا",
    btnServices_en: "Explore Services",
    pillars: ["طب دقيق", "رعاية شاملة", "خبرة دولية"],
    pillars_en: ["Precision Medicine", "Holistic Care", "Global Expertise"],
    stats: [
      { value: "+2,400", label: "مريض سنوياً", label_en: "Patients Annually" },
      { value: "98%", label: "نسبة الرضا", label_en: "Satisfaction Rate" },
      { value: "6", label: "استشاريين", label_en: "Senior Consultants" },
    ],
    backgroundImage: "https://readdy.ai/api/search-image?query=serene%20minimalist%20medical%20clinic%20interior%20soft%20natural%20light%20warm%20cream%20beige%20tones%20clean%20white%20walls%20subtle%20green%20plants%20calm%20peaceful%20healthcare%20environment%20elegant%20simple&width=1440&height=900&seq=hero-ma-calm2&orientation=landscape",
  },
  whyUs: {
    badge: "لماذا ذا مديكال أفينيو؟",
    badge_en: "Why The Medical Avenue?",
    heading: "طب أعمق، أكثر تخصيصًا،",
    heading_en: "Deeper, More Personalized,",
    subHeading: "ومدعوم بالدقة",
    subHeading_en: "Driven by Precision",
    description: "نحن لسنا عيادة أسرع — بل عيادة أعمق. نفكك الحالة ونقرأ ما وراء العرض، ونربط بين الحاضر الصحي والمخاطر المستقبلية.",
    description_en: "We are not a faster clinic — we are a deeper clinic. We deconstruct the condition and look beyond symptoms, linking present wellness with future risks.",
    subDescription: "كل مريض يملك بيولوجيا ومسارًا صحيًا مختلفًا. لذلك نصمم لكل حالة خطة مبنية على تقييمها الفعلي، لا على بروتوكول عام.",
    subDescription_en: "Every patient possesses distinct biology and a unique health trajectory. We design personalized care based on actual evaluation, not generic protocols.",
    mainImage: "https://readdy.ai/api/search-image?query=precision%20medicine%20doctor%20reviewing%20advanced%20diagnostic%20data%20DNA%20genomics%20screens%20modern%20sophisticated%20clinical%20environment%20dark%20teal%20professional%20medical%20science&width=700&height=550&seq=whyus-ma1&orientation=landscape",
    items: [
      { icon: "ri-dna-line", title: "تحليل جيني متقدم", title_en: "Advanced Genomic Analysis", description: "نقرأ خريطتك الجينية لفهم استعداداتك الصحية وتخصيص العلاج.", description_en: "Reading your DNA blueprint to uncover predisposition and personalize therapy." },
      { icon: "ri-heart-pulse-line", title: "تقييم العمر البيولوجي", title_en: "Biological Age Assessment", description: "نقيس عمرك البيولوجي الحقيقي بعيداً عن العمر الزمني.", description_en: "Measuring real biological age distinct from chronological years." },
      { icon: "ri-microscope-line", title: "طب وظيفي متكامل", title_en: "Integrative Functional Medicine", description: "نعالج الأسباب الجذرية لا الأعراض فقط.", description_en: "Treating root causes rather than merely masking symptoms." },
      { icon: "ri-global-line", title: "خبرة دولية", title_en: "International Expertise", description: "شراكات مع مراكز طبية في USA وكوريا وأوروبا.", description_en: "Partnerships with leading centers across the USA, Korea, and Europe." },
    ],
  },
  cta: {
    badge: "ابدأ رحلتك نحو فهم حقيقي",
    badge_en: "Begin Your Journey to True Understanding",
    heading: "جسدك يستحق أن يُفهم، لا أن يُعالج فقط",
    heading_en: "Your body deserves to be understood, not merely treated",
    description: "احجز استشارتك الأولى مع فريقنا المتخصص وابدأ رحلة صحية مبنية على العلم والدقة.",
    description_en: "Book your initial consultation with our specialized team and embark on a scientific, precision-driven health journey.",
    subDescription: "نقدم طبًا أعمق · أكثر تخصيصًا · مدعومًا بالتقنيات الدقيقة والخبرة الدولية",
    subDescription_en: "Deeper · Highly personalized · Powered by precision technology and global expertise",
    btnBook: "احجز موعدك الآن",
    btnBook_en: "Book Your Appointment",
    btnContact: "تواصل معنا",
    btnContact_en: "Contact Us",
  },
  trustBar: {
    items: [
      { icon: "ri-award-line", value: "+2,400", label: "مريض سنوياً", label_en: "Patients Annually" },
      { icon: "ri-star-line", value: "98%", label: "نسبة رضا المرضى", label_en: "Patient Satisfaction" },
      { icon: "ri-user-star-line", value: "6", label: "استشاريين متخصصين", label_en: "Senior Specialists" },
      { icon: "ri-hospital-line", value: "12+", label: "سنة خبرة طبية", label_en: "Years Experience" },
      { icon: "ri-global-line", value: "3", label: "شراكات دولية", label_en: "Global Partnerships" },
    ],
  },
  testimonials: {
    badge: "آراء مرضانا",
    badge_en: "Patient Testimonials",
    heading: "تجارب حقيقية، نتائج ملموسة",
    heading_en: "Real Experiences, Tangible Results",
    description: "ما يقوله مرضانا عن تجربتهم في ذا مديكال أفينيو",
    description_en: "What our patients say about their experience at The Medical Avenue",
    items: [
      {
        name: "أحمد الشمري",
        name_en: "Ahmed Al-Shammari",
        role: "مريض منذ 2023",
        role_en: "Patient since 2023",
        text: "تجربة استثنائية. الفريق الطبي أخذ وقته الكافي لفهم حالتي بشكل كامل وقدم لي خطة علاجية مخصصة غيّرت حياتي.",
        text_en: "An extraordinary experience. The clinical team took extensive time to deeply understand my case and provided a customized plan that transformed my vitality.",
        rating: 5,
        avatar: "https://readdy.ai/api/search-image?query=professional%20arab%20man%20portrait%20headshot%20neutral%20background%20confident%20smile%20clean%20modern%20style&width=80&height=80&seq=t1&orientation=squarish",
      },
      {
        name: "سارة المطيري",
        name_en: "Sara Al-Mutairi",
        role: "مريضة منذ 2024",
        role_en: "Patient since 2024",
        text: "أخيراً وجدت عيادة تتعامل مع الجسم كنظام متكامل. التحليل الجيني الذي أجريته غيّر فهمي لصحتي تماماً.",
        text_en: "Finally found a clinic treating the human body as an integrated system. The genetic profiling completely shifted how I manage my wellness.",
        rating: 5,
        avatar: "https://readdy.ai/api/search-image?query=professional%20arab%20woman%20portrait%20headshot%20neutral%20background%20confident%20smile%20clean%20modern%20style%20hijab&width=80&height=80&seq=t2&orientation=squarish",
      },
      {
        name: "خالد العتيبي",
        name_en: "Khaled Al-Otaibi",
        role: "مريض منذ 2022",
        role_en: "Patient since 2022",
        text: "الدكاترة هنا يستمعون فعلاً. ليس مجرد وصفة طبية سريعة، بل تقييم شامل ومتابعة مستمرة. أنصح به بشدة.",
        text_en: "The physicians here truly listen. Not just a rushed prescription, but thorough assessment and ongoing care. Highly recommended.",
        rating: 5,
        avatar: "https://readdy.ai/api/search-image?query=professional%20saudi%20man%20portrait%20headshot%20neutral%20background%20confident%20smile%20clean%20modern%20style&width=80&height=80&seq=t3&orientation=squarish",
      },
    ],
  },
  lastUpdated: "2026-04-23T08:30:00",
};

export const cmsSectionLabels: Record<string, string> = {
  hero: "قسم الهيرو (Hero)",
  whyUs: "لماذا نحن",
  cta: "قسم الدعوة للعمل (CTA)",
  trustBar: "شريط الثقة",
  testimonials: "آراء المرضى",
};
